import type { HttpCore } from '../http';
import type {
  ActivationExchangeResponse,
  AuthResponse,
  AuthTokens,
  ForgotPasswordRequest,
  LoginRequest,
  OkResponse,
  RegisterRequest,
  ResendVerificationRequest,
  ResetPasswordRequest,
  VerifyEmailRequest,
} from '../types';

/** `/api/v1/auth/**` — anonymous endpoints; the ones that mint tokens store them on the client. */
export function createAuthApi(http: HttpCore) {
  return {
    /** Finishes a first-time social sign-in after the person accepted the terms. Stores the tokens. */
    async ssoComplete(body: { ticket: string; acceptTerms: true; locale?: string }): Promise<AuthResponse> {
      const res = await http.post<AuthResponse>('/auth/sso/complete', body, { auth: false });
      http.setTokens(res);
      return res;
    },
    /** URL that starts a social sign-in; navigate the browser there (it is a redirect chain, not an XHR). */
    ssoStartUrl(provider: string, returnPath?: string): string {
      const base = http.baseUrl ? http.baseUrl.replace(/\/$/, '') : '';
      const q = returnPath ? `?return=${encodeURIComponent(returnPath)}` : '';
      return `${base}/api/v1/auth/sso/${encodeURIComponent(provider)}/start${q}`;
    },
    async register(body: RegisterRequest): Promise<AuthResponse> {
      const res = await http.post<AuthResponse>('/auth/register', body, { auth: false });
      http.setTokens(res);
      return res;
    },
    async login(body: LoginRequest): Promise<AuthResponse> {
      const res = await http.post<AuthResponse>('/auth/login', body, { auth: false });
      http.setTokens(res);
      return res;
    },
    /**
     * Opens the shared demo account for a language — no password, nothing to register (`{ lang: 'nl' }`;
     * an unknown or missing language falls back to English). 404 `demo_unavailable` when the tenant has
     * the feature off or has no demo user for that language.
     *
     * The session it returns carries an empty refresh token on purpose: a demo is never extended, so the
     * hour the access token lasts is the whole of it and the app returns to /login afterwards. Stored the
     * same way as a real sign-in — the client already treats a falsy refresh token as "cannot be renewed"
     * (see `SessionTokens`, and admin impersonation, which has the same shape).
     */
    async demo(body: { lang?: string } = {}): Promise<AuthResponse> {
      const res = await http.post<AuthResponse>('/auth/demo', body, { auth: false });
      http.setTokens(res);
      return res;
    },
    /**
     * Exchanges the one-tap token from an activation mail for a session.
     *
     * Unlike every other token-minting call here it does NOT store the tokens: committing them replaces
     * whoever is signed in on this browser, and when that is a different person they have to be told whose
     * account the link opens first. `/activate` decides, then calls `setTokens` itself.
     *
     * `auth: false` for the same reason as the rest: the request must not carry a stale bearer, so the
     * exchange behaves identically whether or not somebody is already signed in here.
     */
    activationExchange: (body: { token: string }) =>
      http.post<ActivationExchangeResponse>('/auth/activation/exchange', body, { auth: false }),
    /** Manual refresh; the client also does this automatically on a 401 (single-flight). */
    async refresh(): Promise<AuthTokens | null> {
      const refreshToken = http.getTokens()?.refreshToken;
      if (!refreshToken) return null;
      const res = await http.post<AuthTokens>(
        '/auth/refresh',
        { refreshToken },
        { auth: false, skipRefresh: true },
      );
      http.setTokens(res);
      return res;
    },
    /** Revokes the refresh token server-side and always clears local tokens. */
    async logout(): Promise<void> {
      const refreshToken = http.getTokens()?.refreshToken;
      try {
        if (refreshToken) await http.post<OkResponse>('/auth/logout', { refreshToken });
      } finally {
        http.setTokens(null);
      }
    },
    forgotPassword: (body: ForgotPasswordRequest) =>
      http.post<OkResponse>('/auth/forgot-password', body, { auth: false }),
    resetPassword: (body: ResetPasswordRequest) =>
      http.post<OkResponse>('/auth/reset-password', body, { auth: false }),
    verifyEmail: (body: VerifyEmailRequest) =>
      http.post<OkResponse>('/auth/verify-email', body, { auth: false }),
    resendVerification: (body: ResendVerificationRequest = {}) =>
      http.post<OkResponse>('/auth/resend-verification', body),
  };
}

export type AuthApi = ReturnType<typeof createAuthApi>;
